import { useEffect, useRef, useState } from 'react';
import { LibraryManager, ResourceStatus, ResourceType } from 'libs.library_manager';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { hasNewerDatasetVersion } from '../../utils';
/**
 * SSP-3464: detect whether a newer active PUBLISHED version of a chart's
 * dataset exists in the Library.
 *
 * Fires for charts in any status — DRAFT charts use an in-place update flow;
 * PUBLISHED charts use a clone-with-CREATE_VERSION + archive flow on apply
 * (mirroring the dashboard "review versions" pattern). Callers branch on the
 * chart's own status when handling Apply; the hook itself stays
 * status-agnostic.
 *
 * Errors in the version check are swallowed and resolved as "no newer
 * version" so the surrounding UI never blocks on the API call.
 */
export const useDatasetVersionCheck = ({ cubeConfigId, cubeConfigKey, enabled = true, }) => {
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const libraryClientRef = useRef(libraryClient);
    libraryClientRef.current = libraryClient;
    const [isNewerDatasetAvailable, setIsNewerDatasetAvailable] = useState(false);
    const [datasetName, setDatasetName] = useState(undefined);
    useEffect(() => {
        if (!enabled || !cubeConfigId || !cubeConfigKey) {
            setIsNewerDatasetAvailable(false);
            setDatasetName(undefined);
            return;
        }
        let cancelled = false;
        const checkForNewerVersion = async () => {
            try {
                const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
                const [currentDataset, response] = (await Promise.all([
                    libraryManager.getResourceById(cubeConfigId, ResourceType.CUBE_CONFIG),
                    libraryManager.getResources(ResourceType.CUBE_CONFIG, {
                        key: cubeConfigKey,
                        status: ResourceStatus.PUBLISHED,
                    }),
                ]));
                if (cancelled) {
                    return;
                }
                const candidates = (response?.data || []);
                setIsNewerDatasetAvailable(hasNewerDatasetVersion(candidates, cubeConfigId, currentDataset?.version));
                // Prefer the chart's currently-referenced dataset name. Fall back to
                // any sibling resource sharing the same `key` — names are stable
                // across versions, so this still identifies the dataset for the user
                // when the old id is no longer resolvable.
                setDatasetName(currentDataset?.name || candidates[0]?.name);
            }
            catch {
                if (!cancelled) {
                    setIsNewerDatasetAvailable(false);
                    setDatasetName(undefined);
                }
            }
        };
        checkForNewerVersion();
        return () => {
            cancelled = true;
        };
    }, [enabled, cubeConfigId, cubeConfigKey]);
    return { isNewerDatasetAvailable, datasetName };
};
